import { useState } from "react";
import { ComposedModal, ModalBody, ModalHeader } from "@carbon/react";
import i18next from "../../../i18n";
import InviteSupplier from "../../../components/Material/InviteSupplier/InviteSupplier";
import { BuyerSellerResponse, OrderToSubmitRequest } from "../../../types";
import { OrderInvitationContextType } from "../../../context/types";
import MaterialSuppliersSection from "./MaterialSuppliersSection";

type SuppliersCheckboxListProps = {
  suppliers: Record<string, BuyerSellerResponse[]>;
  handleSupplierSelection: (
    supplier: BuyerSellerResponse,
    materialId: string
  ) => void;
  ordersToSubmit: OrderToSubmitRequest[];
  orderInvitation: OrderInvitationContextType[];
  selectedComplexType?: any;
  selectAllTrigger?: boolean;
  handleOrdersToSubmit: (
    invite:
      | { emailsList: string; materialId: string }
      | {
          materialId: string;
          supplierId: string;
          supplierInfo: {
            id: string;
            email: string;
            name: string;
            companyName: string;
            validated: boolean;
          };
        },
    type?: any
  ) => void;
};

const SuppliersCheckboxList = ({
  suppliers,
  handleSupplierSelection,
  ordersToSubmit,
  orderInvitation,
  selectedComplexType,
  selectAllTrigger,
  handleOrdersToSubmit,
}: SuppliersCheckboxListProps) => {
  const [open, setOpen] = useState(false);

  const onCloseInvitation = () => setOpen(false);

  const materialsWithSuppliers = Object.keys(suppliers)
    .map((key) => {
      const materialSuppliers = suppliers[key];
      if (materialSuppliers && materialSuppliers.length > 0) {
        return {
          material: materialSuppliers[0].material,
          suppliers: materialSuppliers,
        };
      }
      return null;
    })
    .filter(Boolean);

  const materialsWithoutSuppliers = ordersToSubmit.filter((order) => {
    const hasSuppliers = Object.keys(suppliers).some((key) => {
      const materialSuppliers = suppliers[key];
      return (
        materialSuppliers &&
        materialSuppliers.length > 0 &&
        materialSuppliers[0].material.id === order.id
      );
    });
    return !hasSuppliers;
  });

  return (
    <fieldset className="order-suppliers">
      {materialsWithSuppliers.map((item) => {
        if (!item) return null;

        return (
          <MaterialSuppliersSection
            key={item.material.id}
            material={{
              id: item.material.id,
              ean: item.material.ean,
              globalMaterialName: item.material.globalMaterialName,
              deliveryPlace: item.material.deliveryPlace,
            }}
            suppliers={item.suppliers}
            orderInvitation={orderInvitation}
            ordersToSubmit={ordersToSubmit}
            selectedComplexType={selectedComplexType}
            selectAllTrigger={selectAllTrigger}
            handleSupplierSelection={handleSupplierSelection}
            handleOrdersToSubmit={handleOrdersToSubmit}
          />
        );
      })}

      {materialsWithoutSuppliers.map((order) => (
        <MaterialSuppliersSection
          key={order.id}
          material={{
            id: order.id || "",
            ean: order.ean || "",
            globalMaterialName: order.globalMaterialName || "",
            deliveryPlace: order.deliveryPlace
              ? { name: order.deliveryPlace.name }
              : undefined,
          }}
          suppliers={[]}
          orderInvitation={orderInvitation}
          ordersToSubmit={ordersToSubmit}
          selectedComplexType={selectedComplexType}
          selectAllTrigger={selectAllTrigger}
          handleSupplierSelection={handleSupplierSelection}
          handleOrdersToSubmit={handleOrdersToSubmit}
        />
      ))}

      <ComposedModal open={open} onClose={() => setOpen(false)} size="sm">
        <ModalHeader title={i18next.t("orderByRequest.inviteSupplier")} />
        <ModalBody hasForm>
          <InviteSupplier
            isOrder
            onCloseInvitation={onCloseInvitation}
            orderInvitation={orderInvitation}
            handleOrdersToSubmit={handleOrdersToSubmit}
          />
        </ModalBody>
      </ComposedModal>
    </fieldset>
  );
};

export default SuppliersCheckboxList;
